/*
 * Nuxt wiring for SignalR notifications (client-only, see nuxt.config: ssr: false).
 *
 * The heavy lifting lives in framework-agnostic modules (reusable by the Customer Portal):
 *   - utils/signalr/signalr-core.js   - which tab acts on a notification (claims, everyTab, dedup)
 *   - utils/signalr/signalr-client.js - master tab election (Web Locks), BroadcastChannel fan-out,
 *                                       reconnect with backoff, connection state
 *
 * This plugin: builds the @microsoft/signalr connection (URL + JWT), starts/stops the client with the
 * auth lifecycle, registers the app-level subscribers (toasts, badge, open-order prompt) and injects
 * $signalr for components. Docs: PIRO.API.v3/Docs/signalR/developer-guide.md.
 */
import * as signalR from '@microsoft/signalr'
import signalRClientFactory from '@/utils/signalr/signalr-client'
import {signalRNotificationTypes} from '@/enums/signalRNotificationTypes'
import {jobTypeCodes} from '@/enums/jobTypeCodes'

export default ({app}, inject) => {
  const defaultAPI = 'http://localhost:55000/api/'
  const apiBaseUrl = app.$config.axios.browserBaseURL || defaultAPI

  let client = null

  // Facade registry: components may subscribe at any time; subscriptions survive the client being
  // recreated on logout/login and are simply parked while nobody is logged in.
  const subscriptions = new Set()

  function subscribe(subscriber) {
    const entry = {subscriber, detach: null}
    subscriptions.add(entry)
    if (client) entry.detach = client.subscribe(subscriber)
    return function unsubscribe() {
      subscriptions.delete(entry)
      if (entry.detach) {
        entry.detach()
        entry.detach = null
      }
    }
  }

  function getToken() {
    const raw = app.$auth && app.$auth.strategy && app.$auth.strategy.token.get()
    if (!raw || typeof raw !== 'string') return ''
    return raw.startsWith('Bearer ') ? raw.slice(7) : raw
  }

  function buildConnection() {
    return new signalR.HubConnectionBuilder()
      .withUrl(`${apiBaseUrl}signalr`, {
        // No skipNegotiation / forced WebSockets: let SignalR fall back to SSE/long polling when a
        // proxy or a flaky network breaks WebSockets. Reconnects are managed by signalr-client.js.
        accessTokenFactory: getToken,
        // The API allows any origin (Access-Control-Allow-Origin: *), which the browser rejects for
        // credentialed requests. We authenticate with the access_token query param, not cookies, so
        // the negotiate/SSE/long-poll requests must be non-credentialed to pass CORS cross-origin.
        withCredentials: false,
      })
      .configureLogging(signalR.LogLevel.Warning)
      .build()
  }

  function setConnectionState(connectionState) {
    app.store.commit('common/signalr/setConnectionState', {connectionState})
  }

  function startClient() {
    if (client || !app.$auth || !app.$auth.loggedIn || !app.$auth.user?.id) return

    client = signalRClientFactory.createSignalRClient({
      buildConnection,
      // Web Locks / BroadcastChannel / localStorage are per browser origin; the user id in the seed
      // keeps two sessions of different users (e.g. incognito re-login) from coordinating.
      namespaceSeed: `${window.location.origin}|${app.$auth.user.id}`,
      onStateChanged: setConnectionState,
    })

    subscriptions.forEach(entry => {
      entry.detach = client.subscribe(entry.subscriber)
    })

    client.setFallbackHandler(notification => {
      // A notification type nothing handles: log only, never bother the user.
      console.warn('[signalr] unhandled notification', notification.notificationType, notification)
    })

    client.start()
  }

  function stopClient() {
    if (!client) return
    subscriptions.forEach(entry => {
      if (entry.detach) {
        entry.detach()
        entry.detach = null
      }
    })
    client.destroy()
    client = null
  }

  // ------------------------------------------------------------ app-level subscribers

  // Generic user notification: the badge count syncs in every tab, the toast appears in exactly one
  // (visible tab preferred - see signalr-core.js claim delays).
  subscribe({
    types: signalRNotificationTypes.General,
    everyTab() {
      app.store.dispatch('common/notification/getNewNotificationCount')
    },
    handle(notification) {
      const message = notification.data?.message
      if (message) app.$toast.success(message)
    },
  })

  subscribe({
    types: signalRNotificationTypes.OpenOrder,
    async handle(notification) {
      const data = notification.data
      if (!data || !data.orderId) {
        console.warn('[signalr] open order notification without orderId', notification)
        return
      }

      const targetPath = `/services/custom-order/${data.orderId}`
      if (app.router.currentRoute.path === targetPath) return

      const root = (app && app.$root) || (typeof window !== 'undefined' && window.$nuxt)

      let orderTypeName = 'custom order'
      switch (data.orderTypeCode) {
        case jobTypeCodes.CustomJobOrder:
          orderTypeName = 'custom order'
          break
        case jobTypeCodes.CustomPriceQuote:
          orderTypeName = 'custom price quote'
          break
        case jobTypeCodes.AppraisalJobOrder:
          orderTypeName = 'appraisal order'
          break
        case jobTypeCodes.AppraisalPriceQuote:
          orderTypeName = 'appraisal price quote'
          break
        default:
          orderTypeName = 'custom order'
          break
      }

      const itemTypeNames = Array.isArray(data.itemTypeNames) ? data.itemTypeNames.join(', ') : ''

      const confirmed = await root.$confirm(
        itemTypeNames ? `New ${orderTypeName} - ${itemTypeNames}` : `New ${orderTypeName}`,
        itemTypeNames
          ? `You have a new ${orderTypeName} (${itemTypeNames}). Do you want to open the order details?`
          : `You have a new ${orderTypeName}. Do you want to open the order details?`,
        {color: '#27333f'}
      )
      if (confirmed) {
        app.router.push(targetPath)
      }
    },
  })

  // ------------------------------------------------------------ auth lifecycle

  app.store.watch(
    state => state.auth && state.auth.loggedIn,
    loggedIn => {
      if (loggedIn) startClient()
      else stopClient()
    }
  )
  // Deferred so $auth (injected by @nuxtjs/auth-next) is present even if this plugin runs first;
  // reading app.$auth synchronously at load can throw and abort client hydration.
  setTimeout(() => {
    if (app.$auth && app.$auth.loggedIn) startClient()
  }, 0)

  inject('signalr', {
    subscribe,
    state: () => (client ? client.state() : {connectionState: 'disconnected', mode: null}),
  })
}
